import { formatRange } from '../../utils/parsing';

import { WatchlistType, WatchlistForListType, WatchlistMatchMode } from '../../types';

type WatchlistLike = Pick<WatchlistForListType, 'routeId' | 'routeName' | 'area' | 'source'>;

export const MATCH_MODE_LABELS: Record<WatchlistMatchMode, string> = {
    within: 'Hela uppdraget sker inom området',
    touches: 'Uppdraget hämtas eller lämnas i området'
};

export const NOTIFICATION_HELP_TEXTS = {
    instantNotifications:
        'Du får en notis via push, sms eller e-post direkt när ett nytt uppdrag eller en ny kapacitet matchar bevakningen.',
    dailyNotifications:
        'Du får en daglig summering av nya uppdrag och kapaciteter som matchar bevakningen via push, sms eller e-post.',
    isJob: 'Bevakningen gäller uppdrag som publiceras av andra.',
    isCapacity: 'Bevakningen gäller kapacitet som publiceras av andra.',
    takesOffer: 'Bevakningen gäller bara uppdrag som tar intresseanmälningar.'
};

export type WatchlistNotificationMode = 'off' | 'instant' | 'daily';

export const NOTIFICATION_MODE_LABELS: Record<WatchlistNotificationMode, string> = {
    off: 'Av',
    instant: 'Direkt',
    daily: 'Dagligen'
};

export const NOTIFICATION_MODE_HELP_TEXTS: Record<WatchlistNotificationMode, string> = {
    off: 'Bevakningen är pausad. Du får inga notiser från den.',
    instant: NOTIFICATION_HELP_TEXTS.instantNotifications,
    daily: 'En sammanställning per dag. Gäller idag DHL-uppdrag.'
};

/**
 * What the segmented control shows. Paused is always "off". If both channels are on (group watchlists
 * start that way) "instant" wins. Active with both channels off matches no option, so null: showing
 * "Av" would claim the watchlist is paused.
 */
export function getNotificationMode(
    w: Pick<WatchlistForListType, 'isActive' | 'instantNotifications' | 'dailyNotifications'>
): WatchlistNotificationMode | null {
    if (!w.isActive) {
        return 'off';
    }
    if (w.instantNotifications) {
        return 'instant';
    }
    return w.dailyNotifications ? 'daily' : null;
}

export type WatchlistTag = 'Rutt' | 'Grupp' | 'Eget område' | 'Hela landet' | 'Pausad';

export function hasRoute(w: WatchlistLike) {
    return w.source === 'route' || w.source === 'residence' || !!w.routeId;
}

export function formatArea(w: WatchlistLike) {
    if (w.routeId) {
        return w.routeName ? `Rutt ${w.routeName}` : 'Rutt';
    }
    if (w.area?.type === 'circle') {
        return `Cirkel, radie ${Math.round(w.area.shape.radius / 1000)} km`;
    }
    if (w.area?.type === 'polygon') {
        return 'Eget område (polygon)';
    }
    return 'Hela landet';
}

export function getWatchlistTags(w: WatchlistLike & { isActive: boolean }): WatchlistTag[] {
    const tags: WatchlistTag[] = [];
    if (w.source === 'group') {
        tags.push('Grupp');
    }
    if (hasRoute(w)) {
        tags.push('Rutt');
    } else if (w.area) {
        tags.push('Eget område');
    } else {
        tags.push('Hela landet');
    }
    if (!w.isActive) {
        tags.push('Pausad');
    }
    return tags;
}

export function formatType({ isJob, isCapacity, takesOffer }: WatchlistForListType | WatchlistType) {
    const types = [];
    if (isJob) {
        types.push(takesOffer ? 'Uppdrag (bara de som tar intresseanmälningar)' : 'Uppdrag');
    }
    if (isCapacity) {
        types.push('Kapacitet');
    }
    return types.join(' & ');
}

export function formatWatchlist(w: WatchlistForListType | WatchlistType) {
    return [
        formatType(w),
        formatArea(w),
        formatRange(w.minGrossWeight, w.maxGrossWeight, 'kg tot'),
        formatRange(w.minChargeableWeight, w.maxChargeableWeight, 'kg ldg'),
        formatRange(w.minVolume, w.maxVolume, 'm³'),
        formatRange(w.minLoadingMeters, w.maxLoadingMeters, 'flm'),
        formatRange(w.minPalletPlaces, w.maxPalletPlaces, 'ppl'),
        formatRange(w.minPallets, w.maxPallets, 'pallar'),
        // formatRange(w.minPieces, w.maxPieces, 'stycken'),
        w.groupName ? `I grupp ${w.groupName}` : '',
        w.hasListPrice ? 'Har riktpris' : ''
    ].filter((v) => !!v).join(', ');
}

export function formatNotificationCount(count: number) {
    return `${count} ${count === 1 ? 'notis' : 'notiser'} senaste 30 dagarna`;
}

/**
 * Keeps the type checkboxes consistent: "Bara uppdrag som tar intresseanmälningar" narrows Uppdrag
 * only (capacity is unaffected), so it requires Uppdrag and is cleared when Uppdrag is turned off.
 */
export function applyTypeRules(change: { isJob?: boolean, isCapacity?: boolean, takesOffer?: boolean }) {
    if (change.takesOffer === true) return { ...change, isJob: true };
    if (change.isJob === false) return { ...change, takesOffer: false };
    return change;
}
